import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Chip,
  Divider,
  Button,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import orderDetailsService from '../services/orderDetailsService';

const OrderDetails = ({ orderId, onBack }) => {
  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const brandColor = 'var(--primary)';

  // Consistent paper styling for all cards
  const paperStyle = {
    elevation: 0,
    sx: {
      p: { xs: 1.5, sm: 2, md: 2 },
      background: 'linear-gradient(135deg, color-mix(in srgb, var(--primary) 2%, transparent) 0%, color-mix(in srgb, var(--primary) 1%, transparent) 100%)',
      border: '1px solid color-mix(in srgb, var(--primary) 8%, transparent)',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
      width: '100%',
      display: 'flex',
      flexDirection: 'column'
    }
  };

  // Fetch order details
  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const result = await orderDetailsService.fetchOrderDetails(orderId);

        if (result.success) {
          setOrder(result.data);
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError('Error loading order details');
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId]);

  // Fetch customer details
  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (!order?.customerId && !order?.userId) return;

      try {
        const customerId = order?.customerId || order?.userId;
        const result = await orderDetailsService.fetchCustomerDetails(customerId);

        if (result.success) {
          setCustomer(result.data);
        }
      } catch (err) {
        console.error('Error fetching customer details:', err);
      }
    };

    if (order) {
      fetchCustomerDetails();
    }
  }, [order]);

  const steps = orderDetailsService.getOrderStatusSteps(order?.deliveryMethod);

  const renderStatusStep = (stepStatus, label, index, totalSteps) => {
    const isComplete = orderDetailsService.isStatusComplete(order?.orderStatus, stepStatus);
    const isCurrent = order?.orderStatus === stepStatus;
    const color = isCurrent
      ? orderDetailsService.getStatusColor(stepStatus)
      : isComplete
        ? '#17ae7b'
        : '#e0e0e0';
    const textColor = isCurrent
      ? orderDetailsService.getStatusColor(stepStatus)
      : isComplete
        ? '#17ae7b'
        : '#999999';

    return (
      <Box key={stepStatus} sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: 1, sm: 1.5 } }}>
        <Box sx={{ mr: { xs: 1, sm: 1.5 }, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '24px' }}>
          {isComplete ? (
            <CheckCircleIcon sx={{ color, fontSize: { xs: 18, sm: 24 } }} />
          ) : (
            <RadioButtonUncheckedIcon sx={{ color, fontSize: { xs: 18, sm: 24 } }} />
          )}
          {index < totalSteps - 1 && (
            <Box
              sx={{
                width: '2px',
                height: { xs: '24px', sm: '30px' },
                bgcolor: color,
                my: 0.3,
              }}
            />
          )}
        </Box>
        <Box sx={{ pt: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: isCurrent ? 'bold' : 'normal',
              color: textColor,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            }}
          >
            {label}
          </Typography>
          {isCurrent && (
            <Typography variant="caption" sx={{ color: '#999999', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
              {orderDetailsService.formatDate(order?.updatedAt)}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: brandColor }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mb: 2, color: brandColor }}
        >
          Back to Orders
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mb: 2, color: brandColor }}
        >
          Back to Orders
        </Button>
        <Alert severity="info">Order not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header with Back Button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: { xs: 1.5, sm: 2.5, md: 3 },
          pb: { xs: 0.75, sm: 1.5, md: 2 },
          borderBottom: '1px solid #e0e0e0',
          px: { xs: 1, sm: 2, md: 3 },
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              color: brandColor,
              mb: { xs: 0.75, sm: 1 },
              textTransform: 'none',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              p: 0,
              '&:hover': { backgroundColor: 'color-mix(in srgb, var(--primary) 5%, transparent)' }
            }}
          >
            Back to Orders
          </Button>
          <Typography variant="h4" sx={{ fontWeight: '700', color: '#1A1A1A', fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.5rem' } }}>
            Order #{order?.orderNumber || order?._id}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: { xs: 0.25, sm: 0.5 }, fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}>
            Ordered on {orderDetailsService.formatDate(order?.transtime)}
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: { xs: 1, sm: 2, md: 2.5 }, width: '100%', margin: '0 auto' }}>
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }} sx={{ minHeight: 'auto' }}>
        {/* Row 1: Order Summary & Order Status */}
        {/* Left: Order Summary */}
        <Grid item xs={12} sm={12} md={6}  sx={{ display: 'flex',width:{ xs: '100%', sm: '100%',md: '50%'} }}>
          {/* Order Summary */}
          <Paper {...paperStyle}>
            <Typography variant="h6" sx={{ fontWeight: '600', mb: { xs: 1.5, sm: 2 }, color: '#1A1A1A', fontSize: { xs: '1rem', sm: '1.1rem', md: '1rem' } }}>
              Order Summary
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, pb: 1.5, gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                Subtotal:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: '500', color: '#1A1A1A', fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                {orderDetailsService.formatCurrency(order?.orderAmountExclDelivery)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, pb: 1.5, gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                Items:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: '500', color: '#1A1A1A', fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                {orderDetailsService.calculateTotalItems(order?.items)} items
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, pb: 1.5, gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                Shipping/Delivery:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: '500', color: '#1A1A1A', fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                {orderDetailsService.formatCurrency(order?.shippingAmount)}
              </Typography>
            </Box>

            <Divider sx={{ my: 2, borderColor: 'color-mix(in srgb, var(--primary) 10%, transparent)' }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: '600', color: '#1A1A1A', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Total:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: '700', color: brandColor, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                {orderDetailsService.formatCurrency(order?.transamount)}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Middle: Delivery Information */}
        <Grid item xs={12} sm={12} md={6}  sx={{ display: 'flex' }}>
          <Paper {...paperStyle}>
            <Typography variant="h6" sx={{ fontWeight: '600', mb: { xs: 1.5, sm: 2 }, color: '#1A1A1A', fontSize: { xs: '1rem', sm: '1.1rem', md: '1rem' } }}>
              Delivery Information
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}>
                Customer Name
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: '500', color: '#1A1A1A', fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                {customer?.firstName} {customer?.lastName}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}>
                Email
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: '500',
                  color: '#1A1A1A',
                  wordBreak: 'break-all',
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                }}
              >
                {customer?.email}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}>
                Phone
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: '500', color: '#1A1A1A', fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                {customer?.phone}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}>
                Delivery Method
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: '500', color: '#1A1A1A', fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                {orderDetailsService.getDeliveryMethodLabel(order?.deliveryMethod)}
              </Typography>
            </Box>

            {order?.deliveryMethod === 'pickup' && order?.pickUpLocationDetails && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Pickup Location
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: '500' }}>
                  {order?.pickUpLocationDetails?.locationName}
                </Typography>
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Payment Method
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: '500' }}>
                {order?.paymentMethod}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Order Date
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: '500' }}>
                {orderDetailsService.formatDate(order?.transtime)}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Right: Order Status - Make Larger */}
        <Grid item xs={12} sm={12} md={6} sx={{ display: 'flex',width:{ xs: '100%', sm: '100%',md: '49%'} }}>
          <Paper {...paperStyle}>
            <Typography variant="h6" sx={{ fontWeight: '600', mb: { xs: 1.5, sm: 2 }, color: '#1A1A1A', fontSize: { xs: '1rem', sm: '1.1rem', md: '1rem' } }}>
              Order Status
            </Typography>

            <Chip
              label={order?.orderStatus}
              sx={{
                bgcolor: orderDetailsService.getStatusColor(order?.orderStatus),
                color: '#fff',
                fontWeight: '600',
                mb: { xs: 1.5, sm: 2 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                height: { xs: '32px', sm: '36px' },
                borderRadius: '8px',
                width: '100%',
                maxWidth: '200px'
              }}
            />

            <Box sx={{ mt: { xs: 1, sm: 1.5 }, mb: { xs: 1.5, sm: 2 } }}>
              {steps.map((step, idx) =>
                renderStatusStep(step.status, step.label, idx, steps.length)
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: { xs: 1, sm: 1.5 }, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
              Last updated: {orderDetailsService.formatDate(order?.updatedAt)}
            </Typography>
          </Paper>
        </Grid>

        {/* Order Items - Full Width */}
        <Grid item xs={12} sm={12} md={6} sx={{ display: 'flex' }}>
          <Paper {...paperStyle}>
            <Typography variant="h6" sx={{ fontWeight: '600', mb: { xs: 1.5, sm: 2 }, color: '#1A1A1A', fontSize: { xs: '1rem', sm: '1.1rem', md: '1rem' } }}>
              Order Items
            </Typography>

            {order?.items?.map((item, idx) => (
              <Box key={idx}>
                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, mb: 2 }}>
                  <Box
                    sx={{
                      width: { xs: 50, sm: 60, md: 70 },
                      height: { xs: 50, sm: 60, md: 70 },
                      minWidth: { xs: 50, sm: 60, md: 70 },
                      bgcolor: '#f5f5f5',
                      borderRadius: '8px',
                      backgroundImage: item?.productImage ? `url(${item.productImage})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: '600', mb: 0.5, color: '#1A1A1A' }}>
                      {item?.productName}
                    </Typography>
                    {item?.variationValues && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}>
                        Variations: {item?.variationValues}
                      </Typography>
                    )}
                    {item?.categoryName && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}>
                        Category: {item?.categoryName}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>Qty: {item?.itemCount}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: '700', color: brandColor, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {orderDetailsService.formatCurrency(item?.itemAmount)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {idx < order.items.length - 1 && <Divider sx={{ my: 2, borderColor: 'color-mix(in srgb, var(--primary) 10%, transparent)' }} />}
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
};

export default OrderDetails;
